let activeUserId: string | null = null;
let activeSurface: "web" | "desktop" = "web";

export const ONBOARDING_LOCAL_STATE_CLEARED_EVENT =
  "denker:onboarding-local-state-cleared";
export const ONBOARDING_DISPLAY_LOCK_STORAGE_KEY =
  "denker.onboarding.display_lock.desktop";
export const ONBOARDING_CARD_PLACEMENT_STORAGE_KEY =
  "denker.onboarding.card_placement.desktop";
export const ONBOARDING_REPLAY_EXISTING_SCREEN_RECORDING_GRANT_KEY =
  "denker.onboarding.replay_existing_screen_recording_grant";
export const ONBOARDING_POST_EVENT_RESTART_ATTEMPTED_KEY =
  "denker.onboarding.post_event_restart_attempted";
export const ONBOARDING_POST_EVENT_PHASE_KEY =
  "denker.onboarding.post_event_phase";
export const ONBOARDING_SCREEN_RECORDING_RESTART_KEY =
  "denker.onboarding.screen_recording_restart";

export const ONBOARDING_LOCAL_STATE_KEYS = [
  "denker.onboarding.intro_seen",
  "denker.onboarding.desktop_intake",
  "denker.onboarding.voice_preflight_ok",
  "denker.onboarding.permissions_deferred",
  "denker.onboarding.runtime_deferred",
  "denker.onboarding.runtime_connection_open",
  "denker.onboarding.post_event_verified",
  ONBOARDING_POST_EVENT_PHASE_KEY,
  ONBOARDING_POST_EVENT_RESTART_ATTEMPTED_KEY,
  ONBOARDING_SCREEN_RECORDING_RESTART_KEY,
  "denker.onboarding.pending_runtime",
  "denker.onboarding.pending_opencode_model",
  "denker.onboarding.screen_recording_attempt",
  "denker.onboarding.screen_recording_opened",
  ONBOARDING_REPLAY_EXISTING_SCREEN_RECORDING_GRANT_KEY,
  "denker.onboarding.completed_repair_prompted.microphone",
  "denker.onboarding.completed_repair_prompted.screen_recording",
  "denker.tts.auto_enabled_once",
  "denker.onboarding.trial-banner-dismissed",
] as const;

export type OnboardingLocalStateKey =
  (typeof ONBOARDING_LOCAL_STATE_KEYS)[number];

export function setOnboardingLocalStateUser(
  userId: string | null,
  surface: "web" | "desktop",
): void {
  activeUserId = userId;
  activeSurface = surface;
}

export function getOnboardingLocalStateUser(): string | null {
  return activeUserId;
}

export function onboardingLocalStateKey(
  key: OnboardingLocalStateKey,
  userId: string | null = activeUserId,
  surface: "web" | "desktop" = activeSurface,
): string {
  return userId ? `${key}.${surface}.user.${userId}` : `${key}.${surface}`;
}

function storage(): Storage | null {
  return typeof window === "undefined" ? null : window.localStorage;
}

export function getOnboardingItem(key: OnboardingLocalStateKey): string | null {
  try {
    return storage()?.getItem(onboardingLocalStateKey(key)) ?? null;
  } catch {
    return null;
  }
}

export function setOnboardingItem(
  key: OnboardingLocalStateKey,
  value: string,
): void {
  try {
    storage()?.setItem(onboardingLocalStateKey(key), value);
  } catch {
    /* ignore */
  }
}

export function removeOnboardingItem(key: OnboardingLocalStateKey): void {
  try {
    storage()?.removeItem(onboardingLocalStateKey(key));
    if (activeUserId) storage()?.removeItem(`${key}.user.${activeUserId}`);
    storage()?.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function getOnboardingBool(key: OnboardingLocalStateKey): boolean {
  return getOnboardingItem(key) === "1";
}

export function setOnboardingBool(
  key: OnboardingLocalStateKey,
  value: boolean,
): void {
  if (value) setOnboardingItem(key, "1");
  else removeOnboardingItem(key);
}

export function clearPostEventRestartAttempt(): void {
  setOnboardingBool(ONBOARDING_POST_EVENT_RESTART_ATTEMPTED_KEY, false);
}

/**
 * Standing choices rather than progress through the flow. A content-version
 * replay keeps them; a manual restart or account deletion clears them.
 */
const ONBOARDING_REPLAY_PRESERVED_KEYS: ReadonlySet<OnboardingLocalStateKey> =
  new Set([
    "denker.onboarding.trial-banner-dismissed",
    "denker.onboarding.runtime_deferred",
    "denker.onboarding.completed_repair_prompted.microphone",
    "denker.onboarding.completed_repair_prompted.screen_recording",
    "denker.tts.auto_enabled_once",
  ]);

export function clearOnboardingLocalState(scope: "all" | "replay"): void {
  for (const key of ONBOARDING_LOCAL_STATE_KEYS) {
    if (scope === "replay" && ONBOARDING_REPLAY_PRESERVED_KEYS.has(key))
      continue;
    removeOnboardingItem(key);
  }
  try {
    storage()?.removeItem(ONBOARDING_DISPLAY_LOCK_STORAGE_KEY);
    storage()?.removeItem(ONBOARDING_CARD_PLACEMENT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  try {
    window.dispatchEvent(new Event(ONBOARDING_LOCAL_STATE_CLEARED_EVENT));
  } catch {
    /* ignore */
  }
}
